import { Component, OnInit } from '@angular/core'
import { CommessaService } from '../services/commessa.service'
import { TreeNode, DialogService } from 'primeng/api'
import { ICommessa } from '../interfaces/ICommessa'
import * as Enum from '../utils/enums'
import * as FileSaver from 'file-saver'
import * as Constants from '../utils/constants'
import { RequestBuilder } from '../classes/request-builder'
import { finalize } from 'rxjs/operators'
import { FilieraModalComponent } from '../filiera-modal/filiera-modal.component'
import { Loading } from '../utils/loading'
import { TranslateService } from '@ngx-translate/core'
import { FilieraService } from '../services/filiera.service'
import { AlertUtil } from '../utils/alert-util'
import { AziendaService } from '../services/azienda.service'
import { EsecutoreModalComponent } from '../esecutore-modal/esecutore-modal.component'
import { AggiudicatarioModalComponent } from '../aggiudicatario-modal/aggiudicatario-modal.component';

@Component({
  selector: 'app-filiera',
  templateUrl: './filiera.component.html',
  styleUrls: ['./filiera.component.css'],
})
export class FilieraComponent implements OnInit {
  commessaSelected: ICommessa
  commesse: any[] // table data source

  nodes: TreeNode[] = []
  selectedNode: TreeNode

  rowSelected: boolean = false //determines whether one commessa row has been clicked

  DTT = Enum.DATA_TABLE_TYPE_ENUM //export enum for html
  L = Loading //export loading for html

  isPrefettura: boolean
  isAggiudicatario: boolean
  isAffidatario: boolean

  constructor(
    private filieraService: FilieraService,
    private aziendaService: AziendaService,
    private commessaService: CommessaService,
    public dialogService: DialogService,
    private translate: TranslateService,
    private alertUtil: AlertUtil
  ) {
    this.isPrefettura = false
    this.isAggiudicatario = false
    this.isAffidatario = false

    let userRole = localStorage.getItem(Constants.LS_USER_ROLE)
    this.checkUserRole(userRole)
  }

  ngOnInit() {
    setTimeout(() => {
      this.getAllCommesse()
    }, 0)
  }

  /**
   * check role of user logged
   */
  checkUserRole(userRole: string) {
    if (userRole == Enum.USER_TYPE_ENUM.AGGIUDICATARIO) {
      this.isAggiudicatario = true
    } else if (
      userRole == Enum.USER_TYPE_ENUM.PREFETTURA ||
      userRole == Enum.USER_TYPE_ENUM.DL
    ) {
      this.isPrefettura = true
    } else if (userRole == Enum.USER_TYPE_ENUM.AFFIDATARIO) {
      this.isAffidatario = true
    }
  }

  /**
   * retrieve all commesse
   */
  getAllCommesse() {
    Loading.isLoading = true
    let reqBuilder = new RequestBuilder({})
    this.commessaService
      .getAllCommesse(reqBuilder)
      .pipe(
        finalize(() => {
          Loading.isLoading = false
        })
      )
      .subscribe(
        (response) => {
          if (response.data) {
            this.commesse = response.data
          } else if (response.error) {
            let errorKey = 'LBL_ERROR_' + response.error.Code
            this.alertUtil.showErrorAlert(errorKey)
            console.log('ERROR: ' + response.error.Stack)
          } else {
            this.alertUtil.showErrorAlert('LBL_SERVER_ERROR_GENERIC', {
              error: response.Message,
            })
          }
        },
        (error) => {
          let errorKey = 'LBL_HTTP_ERROR_0'
          if (error.status) {
            errorKey = 'LBL_HTTP_ERROR_' + error.status
          }
          this.alertUtil.showErrorAlert(errorKey)
        }
      )
  }

  /**
   * callback fired when one commessa row is selected
   * @param commessa
   */
  onCommessaRowSelected(commessa: ICommessa) {
    Loading.isLoading = true
    this.commessaSelected = commessa
    this.rowSelected = true
    let requestBuilder = new RequestBuilder({ Id: this.commessaSelected.Id })
    this.filieraService
      .getFilieraData(requestBuilder)
      .pipe(
        finalize(() => {
          Loading.isLoading = false
        })
      )
      .subscribe(
        (response) => {
          if (response.data) {
            this.buildTree(response.data)
          } else if (response.error) {
            let errorKey = 'LBL_ERROR_' + response.error.Code
            this.alertUtil.showErrorAlert(errorKey)
            console.log('ERROR: ' + response.error.Stack)
          } else {
            this.alertUtil.showErrorAlert('LBL_SERVER_ERROR_GENERIC', {
              error: response.Message,
            })
          }
        },
        (error) => {
          let errorKey = 'LBL_HTTP_ERROR_0'
          if (error.status) {
            errorKey = 'LBL_HTTP_ERROR_' + error.status
          }
          this.alertUtil.showErrorAlert(errorKey)
        }
      )
  }

  /**
   * callback fired when one commessa row is unselected
   */
  onCommessaRowUnselected() {
    this.rowSelected = false
    this.commessaSelected = null
    this.nodes = null
  }

  /**
   * build tree from commessa
   * @param commessa
   */
  buildTree(commessa: ICommessa) {
    if (commessa.Aggiudicatario) {
      this.nodes = []
      // get tree nodes recursively
      this.getTreeNode(commessa.Aggiudicatario)
    }
  }

  /**
   * get tree node recursively
   * @param curElement
   * @param nodeParent
   */
  getTreeNode(curElement: any, nodeParent?: any) {
    if (curElement) {
      //create current tree node
      let child = {
        label: curElement.Ragione_sociale,
        type: nodeParent ? 'esecutore' : 'aggiudicatario_filiera', //node parent undefined means this node is root and then this node is aggiudicatario
        expanded: true,
        data: curElement,
        children: [],
      }

      if (curElement.Esecutori && curElement.Esecutori.length > 0) {
        // build esecutori recursively
        curElement.Esecutori.forEach((esecutoreChild) => {
          this.getTreeNode(esecutoreChild, child)
        })
      }

      if (curElement.Persone && curElement.Persone.length > 0) {
        //   // insert persona node into persona container
        let personaChild = {
          label: 'PERSONE',
          type: 'persona',
          expanded: true,
          data: curElement.Persone,
          children: [],
        }
        child.children.push(personaChild)
      }

      if (curElement.Mezzi && curElement.Mezzi.length > 0) {
        let mezzoChild = {
          label: 'MEZZI',
          type: 'mezzo',
          expanded: true,
          data: curElement.Mezzi,
          children: [],
        }
        // insert mezzo node into mezzi container
        child.children.push(mezzoChild)
        // });
      }
      //push child element into node parent if exists, push into nodes otherwise (first element)
      if (nodeParent) {
        nodeParent.children.push(child)
      } else {
        this.nodes.push(child)
      }
    }
  }

  /**
   * Select one Node
   * @param event
   */
  onNodeSelect(event: any) {
    let nodeType = event.node.type
    let langKey = this.getLangKeyFromType(event.node.type)
    if (nodeType == Enum.DATA_TABLE_TYPE_ENUM.ESECUTORE) {
      // only aggiudicatario, affidatatrio o prefettura can open esecutore modal
      if (this.isAffidatario || this.isAggiudicatario || this.isPrefettura) {
        let ref = this.dialogService.open(EsecutoreModalComponent, {
          data: {
            esecutore: event.node.data,
            fromFiliera: true,
          },
          header: 'Esecutore',
          width: '40%',
        })

        // refresh filiera when esecutore has been saved
        ref.onClose.subscribe((esecutoreObj) => {
          if (esecutoreObj) {
            this.onCommessaRowSelected(this.commessaSelected)
          }
        })
      }
    } else if (
      nodeType == Enum.DATA_TABLE_TYPE_ENUM.AGGIUDICATARIO ||
      nodeType == Enum.DATA_TABLE_TYPE_ENUM.AGGIUDICATARIO_FILIERA
    ) {
      if (this.isPrefettura || this.isAffidatario || this.isAggiudicatario) {
        //TODO aprire modale descrittivo
        let ref = this.dialogService.open(AggiudicatarioModalComponent, {
          data: {
            aggiudicatario: event.node.data, 
            fromFiliera: true,
          },
          header: 'Aggiudicatario',
          width: '40%',
        })
 
        // refresh filiera when esecutore has been saved
        ref.onClose.subscribe((esecutoreObj) => {
          if (esecutoreObj) {
            this.onCommessaRowSelected(this.commessaSelected)
          }
        })
      }
    } else { // Nodo di Mezzi o Persone
      this.translate.get(langKey).subscribe((res) => {
        let ref = this.dialogService.open(FilieraModalComponent, {
          data: {
            data: event.node.data,
            type: event.node.type,
          },
          header: res,
          width: '70%',
        })
      })
    }
  }

  /**
   * Get language key by type
   * @param type
   */
  getLangKeyFromType(type: any): string {
    let langKey
    if (type == Enum.DATA_TABLE_TYPE_ENUM.PERSONA) {
      langKey = 'LBL_PEOPLE'
    } else if (type == Enum.DATA_TABLE_TYPE_ENUM.MEZZO) {
      langKey = 'LBL_CARS'
    } else if (type == Enum.DATA_TABLE_TYPE_ENUM.AGGIUDICATARIO_FILIERA) {
      langKey = 'LBL_AGGIUDICATARIO'
    } else if (type == Enum.DATA_TABLE_TYPE_ENUM.ESECUTORE) {
      langKey = 'LBL_ESECUTORE'
    }
    return langKey
  }

  /**
   * open new page with link address
   * @param commessa
   */
  goToGISPage(commessa: ICommessa) {
    window.open(commessa.LinkGIS, '_blank')
  }

  /**
   * callback fired when download filiera button is pressed
   */
  onDownloadFiliera() {
    Loading.isLoading = true
    let requestBuilder = new RequestBuilder(this.commessaSelected)
    this.aziendaService
      .exportFiliera(requestBuilder)
      .pipe(
        finalize(() => {
          Loading.isLoading = false
        })
      )
      .subscribe(
        (response) => {
          if (response && response['error'] == null) {
            this.downloadFile(
              response,
              Enum.FILE_TYPE_ENUM.XLSX,
              'CensimentoAziende'
            )
          } else if (response.error) {
            let errorKey = 'LBL_ERROR_' + response.error.Code
            this.alertUtil.showErrorAlert(errorKey)
            console.log('ERROR: ' + response.error.Stack)
          } else {
            this.alertUtil.showErrorAlert('LBL_SERVER_ERROR_GENERIC', {
              error: response.Message,
            })
          }
        },
        (error) => {
          let errorKey = 'LBL_HTTP_ERROR_0'
          if (error.status) {
            errorKey = 'LBL_HTTP_ERROR_' + error.status
          }
          this.alertUtil.showErrorAlert(errorKey)
        }
      )
  }

  /**
   * download zip file
   * @param response response from service
   * @param type type of file
   * @param name name of file
   */
  downloadFile(response: any, type: string, name: string) {
    let fileType = ''
    let stringDecoded: any
    //switch type of file and set fileType and string decoded
    switch (type) {
      case Enum.FILE_TYPE_ENUM.XLS:
        fileType = 'application/vnd.ms-excel;charset=utf-8'
        stringDecoded = this.s2ab(atob(response))
        break
      case Enum.FILE_TYPE_ENUM.XLSX:
        fileType =
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8'
        stringDecoded = this.s2ab(atob(response))
        break
      case Enum.FILE_TYPE_ENUM.DOC:
      case Enum.FILE_TYPE_ENUM.DOCX:
        fileType = 'application/msword;charset=utf-8'
        stringDecoded = this.s2ab(atob(response))
        break
      case Enum.FILE_TYPE_ENUM.PPT:
      case Enum.FILE_TYPE_ENUM.PPTX:
        fileType = 'application/vnd.ms-powerpoint;charset=utf-8'
        stringDecoded = this.s2ab(atob(response))
        break
      case Enum.FILE_TYPE_ENUM.ZIP:
        fileType = 'application/zip'
        stringDecoded = this.s2ab(atob(response))
        break
      default: //case Enum.FILE_TYPE_ENUM.TXT:
        fileType = 'text/plain;charset=utf-8'
        stringDecoded = atob(response)
    }

    let blob = new Blob([stringDecoded], {
      type: fileType,
    })
    FileSaver.saveAs(blob, name)
  }

  private s2ab(s: any) {
    var buf = new ArrayBuffer(s.length)
    var view = new Uint8Array(buf)
    for (var i = 0; i != s.length; ++i) view[i] = s.charCodeAt(i) & 0xff
    return buf
  }
}
